import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FaCrop, FaSave, FaUndo } from 'react-icons/fa';
import { API_ENDPOINTS } from '../config/backendConfig';

/**
 * StoryboardingCropper
 * Props:
 *   imageUrl: string (refined artwork to crop from)
 *   tags: array (tags with positions)
 *   onSave: function(savedProfiles)
 *   onCancel: function()
 */
export default function StoryboardingCropper({ imageUrl, tags, onSave, onCancel }) {
  const [cropBoxes, setCropBoxes] = useState({});
  const [saving, setSaving] = useState(false);
  const imageRef = useRef(null);

  // Initialize crop boxes for each tag
  useEffect(() => {
    const initialCropBoxes = {};
    tags.forEach(tag => {
      initialCropBoxes[tag.id] = {
        x: Math.max(0, tag.position.x - 0.1),
        y: Math.max(0, tag.position.y - 0.1),
        width: 0.2,
        height: 0.2
      };
    });
    setCropBoxes(initialCropBoxes);
  }, [tags]);

  const handleCropBoxChange = (tagId, newCropBox) => {
    setCropBoxes(prev => ({
      ...prev,
      [tagId]: newCropBox
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(API_ENDPOINTS.storyboardingSaveCharacterProfile, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          refinedImageUrl: imageUrl,
          tags: tags.map(tag => ({
            ...tag,
            cropBox: cropBoxes[tag.id]
          })),
          userId: 'current-user-id' // TODO: Get from auth context
        })
      });
      const data = await response.json();
      onSave(data.profiles);
    } catch (error) {
      console.error('Failed to save character profiles:', error);
      alert('Failed to save character profiles');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-amber-200">
          <h3 className="text-xl font-bold text-amber-800">Crop Character Profiles</h3>
          <button
            className="text-gray-500 hover:text-gray-700 text-2xl"
            onClick={onCancel}
            title="Cancel"
          >
            ×
          </button>
        </div>
        
        <div className="p-6">
          <div className="relative w-full max-h-[60vh] overflow-auto">
            <img
              ref={imageRef}
              src={imageUrl}
              alt="Refined artwork for cropping"
              className="w-full h-auto"
            />
            {/* Render crop boxes */}
            {tags.map(tag => {
              const cropBox = cropBoxes[tag.id];
              if (!cropBox) return null;
              
              return (
                <div
                  key={tag.id}
                  className="absolute border-2 border-red-500 bg-red-500/20 cursor-move"
                  style={{
                    left: `${cropBox.x * 100}%`,
                    top: `${cropBox.y * 100}%`,
                    width: `${cropBox.width * 100}%`,
                    height: `${cropBox.height * 100}%`
                  }}
                  draggable
                  onDragEnd={(e) => {
                    // TODO: Implement drag logic to update crop box position
                    console.log('Drag ended for tag:', tag.id);
                  }}
                >
                  <div className="absolute -top-6 left-0 bg-red-500 text-white px-2 py-1 rounded text-xs">
                    {tag.name}
                  </div>
                </div>
              );
            })}
          </div>
          
          <div className="mt-6 flex justify-center space-x-4">
            <button
              onClick={onCancel}
              className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-3 rounded-xl flex items-center space-x-2"
            >
              <FaUndo />
              <span>Cancel</span>
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="bg-green-500 hover:bg-green-600 disabled:opacity-50 text-white px-6 py-3 rounded-xl flex items-center space-x-2"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <FaSave />
                  <span>Save Profiles</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

StoryboardingCropper.propTypes = {
  imageUrl: PropTypes.string.isRequired,
  tags: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    position: PropTypes.shape({
      x: PropTypes.number.isRequired,
      y: PropTypes.number.isRequired
    }).isRequired
  })).isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
}; 